import { useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Check, ChevronsUpDown } from 'lucide-react';
import { type Dispatch, type SetStateAction, useCallback, useEffect, useMemo, useState } from 'react';

import { Button } from '~/components/shadcn/button.tsx';
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuGroup,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuRadioGroup,
	DropdownMenuRadioItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from '~/components/shadcn/dropdown-menu.tsx';
import { Input } from '~/components/shadcn/input.tsx';
import { Popover, PopoverContent, PopoverTrigger } from '~/components/shadcn/popover.tsx';
import { Skeleton } from '~/components/shadcn/skeleton.tsx';
import { Tabs, TabsList, TabsTrigger } from '~/components/shadcn/tabs.tsx';
import { useTranslations } from '~/i18n/use-translations.ts';
import type { RouterContext } from '~/router-context.ts';
import { requireAuthenticatedBeforeLoad } from '~/routing/require-authenticated-before-load.ts';
import { cn } from '~/utils/cn.ts';

import { CalendarTable } from '../components/news/calendar-table.tsx';
import { NewsTable } from '../components/news/news-table.tsx';
import { useSupabase } from '../integrations/supabase/provider.tsx';
import type { CompanyFilterOption } from '../server/news/get-news-page-data.ts';
import { getNewsPageDataQueryOptions } from '../server/news/news-page-data-query.ts';
import { invalidateNewsQueries } from '../utils/query-invalidation.ts';

type NewsScope = 'all' | 'interest';

type NewsView = 'calendar' | 'news';

interface NewsLoaderDeps {
	readonly company?: string;
	readonly type?: NewsScope;
}

interface NewsFilters {
	companyId: string;
	language: string;
	searchQuery: string;
	view: NewsView;
}

interface NewsSearch {
	company?: string;
	lang?: string;
	q?: string;
	type?: NewsScope;
	view?: NewsView;
}

function normalizeNewsLanguage(value: string | null | undefined): string | null {
	if (!value) {
		return null;
	}

	return value.toLowerCase().split(/[-_]/)[0] ?? null;
}

function getLocaleLanguageCode(locale: string): string {
	const code = locale.toLowerCase().split(/[-_]/)[0];

	return code || 'en';
}

function getNewsScope(type: string | null | undefined): NewsScope {
	return type === 'interest' ? 'interest' : 'all';
}

function getNewsView(view: string | null | undefined): NewsView {
	return view === 'calendar' ? 'calendar' : 'news';
}

function validateNewsSearch(search: Record<string, unknown>): NewsSearch {
	return {
		company: typeof search.company === 'string' ? search.company : undefined,
		lang: typeof search.lang === 'string' ? search.lang : undefined,
		q: typeof search.q === 'string' ? search.q : undefined,
		type: search.type === 'interest' ? 'interest' : search.type === 'all' ? 'all' : undefined,
		view: search.view === 'calendar' ? 'calendar' : search.view === 'news' ? 'news' : undefined,
	};
}

function buildNewsSearch(scope: NewsScope, filters: NewsFilters): NewsSearch {
	const trimmedQuery = filters.searchQuery.trim();

	return {
		company: filters.companyId || undefined,
		lang: filters.language,
		q: trimmedQuery.length > 0 ? trimmedQuery : undefined,
		type: scope,
		view: filters.view,
	};
}

function isSameNewsSearch(left: NewsSearch, right: NewsSearch): boolean {
	return (
		left.company === right.company &&
		left.lang === right.lang &&
		left.q === right.q &&
		left.type === right.type &&
		left.view === right.view
	);
}

export const Route = createFileRoute('/news')({
	beforeLoad: requireAuthenticatedBeforeLoad,
	component: NewsPage,
	loader: ({ context, deps }: { context: RouterContext; deps: NewsLoaderDeps }) =>
		context.queryClient.ensureQueryData(
			getNewsPageDataQueryOptions({
				company: deps.company,
				type: deps.type,
			}),
		),
	loaderDeps: ({ search }) => ({
		company: 'company' in search ? search.company : undefined,
		type: 'type' in search ? search.type : undefined,
	}),
	pendingComponent: NewsPagePending,
	pendingMinMs: 250,
	validateSearch: validateNewsSearch,
});

function NewsPagePending() {
	return (
		<div className="flex h-full w-full flex-col gap-2 p-2">
			<div className="flex flex-wrap items-center justify-between gap-2 border border-border bg-card px-3 py-2">
				<div className="flex flex-wrap items-center gap-2">
					<Skeleton className="h-8 w-20 rounded-md" />
					<Skeleton className="h-8 w-24 rounded-md" />
					<Skeleton className="h-8 w-[220px] rounded-md" />
					<Skeleton className="h-8 w-[220px] rounded-md" />
					<Skeleton className="h-8 w-24 rounded-md" />
				</div>
				<Skeleton className="h-8 w-28 rounded-md" />
			</div>

			<div className="grid h-news-body-height grid-cols-1 gap-2 md:grid-cols-3">
				<Skeleton className="min-h-[320px] w-full rounded-md border border-border bg-card md:col-span-2" />
				<Skeleton className="min-h-[320px] w-full rounded-md border border-border bg-card md:col-span-1" />
			</div>
		</div>
	);
}

function NewsPage() {
	const t = useTranslations();
	const navigate = Route.useNavigate();
	const search = Route.useSearch();
	const queryClient = useQueryClient();
	const { data } = useSuspenseQuery(
		getNewsPageDataQueryOptions({
			company: search.company,
			type: search.type,
		}),
	);
	const { calendar, companyFilterOptions, news, userLocale } = data;
	const [tableFormFactor, setTableFormFactor] = useState<'col' | 'row'>('row');
	const preferredLanguage = useMemo(() => {
		const localeLanguage = getLocaleLanguageCode(userLocale);
		const availableLanguages = new Set(
			(news?.data ?? [])
				.map((row) => normalizeNewsLanguage(row.articles?.lang))
				.filter((language): language is string => Boolean(language)),
		);

		return availableLanguages.has(localeLanguage) ? localeLanguage : 'en';
	}, [news?.data, userLocale]);
	const companyIdSet = useMemo(
		() => new Set(companyFilterOptions.map((company) => company.id)),
		[companyFilterOptions],
	);
	const scope = getNewsScope(search.type);
	const filters = useMemo<NewsFilters>(() => {
		const languageParam = search.lang;
		const language =
			languageParam === 'en' && preferredLanguage !== 'en'
				? 'en'
				: languageParam === preferredLanguage || preferredLanguage === 'en'
					? preferredLanguage
					: preferredLanguage;
		const searchQuery = search.q ?? '';
		const companyId = search.company && companyIdSet.has(search.company) ? search.company : '';

		return {
			companyId,
			language,
			searchQuery,
			view: getNewsView(search.view),
		};
	}, [companyIdSet, preferredLanguage, search.company, search.lang, search.q, search.view]);

	const applyNewsFiltersToSearch = useCallback(
		(nextScope: NewsScope, nextFilters: NewsFilters, replace: boolean = true) => {
			void navigate({
				replace,
				search: buildNewsSearch(nextScope, nextFilters),
				to: '/news',
			});
		},
		[navigate],
	);

	useEffect(() => {
		const normalizedSearch = buildNewsSearch(scope, filters);

		if (isSameNewsSearch(search, normalizedSearch)) {
			return;
		}

		void navigate({
			replace: true,
			search: normalizedSearch,
			to: '/news',
		});
	}, [filters, navigate, scope, search]);

	const setFilters = useCallback(
		(patch: Partial<NewsFilters>) => {
			applyNewsFiltersToSearch(
				scope,
				{
					...filters,
					...patch,
				},
				true,
			);
		},
		[applyNewsFiltersToSearch, filters, scope],
	);

	const setScope = useCallback(
		(nextScope: NewsScope) => {
			applyNewsFiltersToSearch(nextScope, filters, false);
		},
		[applyNewsFiltersToSearch, filters],
	);

	const clearFilters = useCallback(() => {
		setFilters({
			companyId: '',
			language: preferredLanguage,
			searchQuery: '',
		});
	}, [preferredLanguage, setFilters]);

	const { accessToken, supabaseClient } = useSupabase();

	useEffect(() => {
		if (!supabaseClient || !accessToken) {
			return;
		}

		const channel = supabaseClient
			.channel('news_updates')
			.on(
				'postgres_changes',
				{
					event: 'INSERT',
					schema: 'public',
					table: 'news',
				},
				() => {
					void invalidateNewsQueries(queryClient);
				},
			)
			.subscribe();

		channel.socket.setAuth(accessToken);

		return () => {
			void supabaseClient.removeChannel(channel);
		};
	}, [accessToken, queryClient, supabaseClient]);

	return (
		<div className="flex h-full w-full flex-col gap-2 p-2">
			<div className="flex flex-wrap items-center justify-between gap-2 border border-border bg-card px-3 py-2">
				<div className="flex flex-wrap items-center gap-2">
					<LayoutSettings formFactor={tableFormFactor} setFormFactor={setTableFormFactor} />
					<LanguageSettings
						preferredLanguage={preferredLanguage}
						selectedLanguage={filters.language}
						setSelectedLanguage={(language) => {
							setFilters({
								language,
							});
						}}
					/>
					<Input
						aria-label={t('news.filterText')}
						className="w-full sm:w-[220px]"
						onChange={(event) =>
							setFilters({
								searchQuery: event.currentTarget.value,
							})
						}
						placeholder={t('news.searchHeadlines')}
						type="search"
						value={filters.searchQuery}
					/>
					<CompanyFilter
						companies={companyFilterOptions}
						onCompanyChange={(companyId) =>
							setFilters({
								companyId,
							})
						}
						selectedCompanyId={filters.companyId}
					/>
					<Button aria-label={t('news.clearFilters')} onClick={clearFilters} size="xs" type="button" variant="outline">
						{t('news.clearFilters')}
					</Button>
				</div>
				<InterestToggle onScopeChange={setScope} scope={scope} />
			</div>

			<div className="md:hidden">
				<Tabs
					onValueChange={(value) =>
						setFilters({
							view: value === 'calendar' ? 'calendar' : 'news',
						})
					}
					value={filters.view}
				>
					<TabsList aria-label={t('news.viewSelection')} className="w-full justify-start">
						<TabsTrigger value="news">{t('nav.news')}</TabsTrigger>
						<TabsTrigger value="calendar">{t('nav.calendar')}</TabsTrigger>
					</TabsList>
				</Tabs>
			</div>

			<div className="grid h-news-body-height grid-cols-1 gap-2 md:grid-cols-3">
				<div className={cn(filters.view === 'news' ? 'block' : 'hidden', 'min-h-0 md:col-span-2 md:block')}>
					<NewsTable
						companyFilterId={filters.companyId}
						languageFilter={filters.language}
						news={news?.data ?? []}
						onClearFilters={clearFilters}
						page={news?.page ?? 0}
						scope={scope}
						searchQuery={filters.searchQuery}
						tableFormFactor={tableFormFactor}
						userLocale={userLocale}
					/>
				</div>
				<div className={cn(filters.view === 'calendar' ? 'block' : 'hidden', 'min-h-0 md:col-span-1 md:block')}>
					<CalendarTable
						calendar={calendar?.data ?? []}
						page={calendar?.page ?? 0}
						scope={scope}
						userLocale={userLocale}
					/>
				</div>
			</div>
		</div>
	);
}

function LayoutSettings({
	formFactor,
	setFormFactor,
}: Readonly<{
	formFactor: 'col' | 'row';
	setFormFactor: Dispatch<SetStateAction<'col' | 'row'>>;
}>) {
	const t = useTranslations();
	return (
		<DropdownMenu>
			<DropdownMenuTrigger asChild title={t('news.displayTitle')}>
				<Button size="xs" type="button" variant="outline">
					{t('news.display')}
				</Button>
			</DropdownMenuTrigger>
			<DropdownMenuContent>
				<DropdownMenuGroup>
					<DropdownMenuLabel>{t('news.displayFormat')}</DropdownMenuLabel>
					<DropdownMenuSeparator />
					<DropdownMenuItem
						className={cn(formFactor === 'col' ? 'underline' : '')}
						onClick={() => setFormFactor('col')}
					>
						{t('news.columnFormat')}
					</DropdownMenuItem>
					<DropdownMenuItem
						className={cn(formFactor === 'row' ? 'underline' : '')}
						onClick={() => setFormFactor('row')}
					>
						{t('news.rowFormat')}
					</DropdownMenuItem>
				</DropdownMenuGroup>
			</DropdownMenuContent>
		</DropdownMenu>
	);
}

function LanguageSettings({
	preferredLanguage,
	selectedLanguage,
	setSelectedLanguage,
}: Readonly<{
	preferredLanguage: string;
	selectedLanguage: string;
	setSelectedLanguage: (language: string) => void;
}>) {
	const t = useTranslations();
	const shouldShowEnglishAlternative = preferredLanguage !== 'en';

	return (
		<DropdownMenu>
			<DropdownMenuTrigger asChild title={t('news.filterLanguage')}>
				<Button size="xs" type="button" variant="outline">
					{t('news.languageShort', { language: selectedLanguage.toUpperCase() })}
				</Button>
			</DropdownMenuTrigger>
			<DropdownMenuContent>
				<DropdownMenuLabel>{t('news.language')}</DropdownMenuLabel>
				<DropdownMenuSeparator />
				<DropdownMenuRadioGroup onValueChange={setSelectedLanguage} value={selectedLanguage}>
					<DropdownMenuRadioItem value={preferredLanguage}>
						{t('news.userLanguage', { language: preferredLanguage.toUpperCase() })}
					</DropdownMenuRadioItem>
					{shouldShowEnglishAlternative ? (
						<DropdownMenuRadioItem value="en">{t('news.english')}</DropdownMenuRadioItem>
					) : null}
				</DropdownMenuRadioGroup>
			</DropdownMenuContent>
		</DropdownMenu>
	);
}

function CompanyFilter({
	companies,
	selectedCompanyId,
	onCompanyChange,
}: Readonly<{
	companies: CompanyFilterOption[];
	selectedCompanyId: string;
	onCompanyChange: (companyId: string) => void;
}>) {
	const t = useTranslations();
	const [isOpen, setIsOpen] = useState(false);
	const [searchTerm, setSearchTerm] = useState('');
	const selectedCompany = companies.find((company) => company.id === selectedCompanyId);
	const filteredCompanies = useMemo(
		() => companies.filter((company) => company.name.toLowerCase().includes(searchTerm.trim().toLowerCase())),
		[companies, searchTerm],
	);

	return (
		<Popover onOpenChange={setIsOpen} open={isOpen}>
			<PopoverTrigger asChild>
				<Button
					aria-expanded={isOpen}
					aria-label={t('news.filterCompany')}
					className="w-full justify-between sm:w-[220px]"
					size="xs"
					type="button"
					variant="outline"
				>
					<span className="truncate">{selectedCompany?.name ?? t('news.allCompanies')}</span>
					<ChevronsUpDown className="ml-2 size-4 shrink-0 opacity-50" />
				</Button>
			</PopoverTrigger>
			<PopoverContent align="start" className="w-[320px] p-2">
				<Input
					aria-label={t('news.searchCompanies')}
					className="mb-2"
					onChange={(event) => setSearchTerm(event.currentTarget.value)}
					placeholder={t('news.searchCompaniesPlaceholder')}
					type="search"
					value={searchTerm}
				/>
				<div className="max-h-64 overflow-y-auto border border-border">
					<button
						className={cn(
							'flex w-full items-center justify-between px-2 py-1.5 text-left text-xs hover:bg-accent',
							!selectedCompanyId ? 'bg-accent' : '',
						)}
						onClick={() => {
							onCompanyChange('');
							setIsOpen(false);
						}}
						type="button"
					>
						<span>{t('news.allCompanies')}</span>
						{!selectedCompanyId ? <Check className="size-4" /> : null}
					</button>
					{filteredCompanies.map((company) => {
						const isSelected = selectedCompanyId === company.id;

						return (
							<button
								className={cn(
									'flex w-full items-center justify-between px-2 py-1.5 text-left text-xs hover:bg-accent',
									isSelected ? 'bg-accent' : '',
								)}
								key={company.id}
								onClick={() => {
									onCompanyChange(company.id);
									setIsOpen(false);
								}}
								type="button"
							>
								<span className="truncate">{company.name}</span>
								{isSelected ? <Check className="size-4" /> : null}
							</button>
						);
					})}
					{filteredCompanies.length === 0 ? (
						<div className="px-2 py-2 text-muted-foreground text-xs">{t('news.noMatchingCompanies')}</div>
					) : null}
				</div>
			</PopoverContent>
		</Popover>
	);
}

function InterestToggle({
	scope,
	onScopeChange,
}: Readonly<{
	scope: NewsScope;
	onScopeChange: (scope: NewsScope) => void;
}>) {
	const t = useTranslations();
	const isInterested = scope === 'interest';

	return (
		<div className="ml-auto flex items-center gap-1">
			<Button
				aria-label={t('news.showAll')}
				onClick={() => onScopeChange('all')}
				size="xs"
				type="button"
				variant={!isInterested ? 'secondary' : 'outline'}
			>
				{t('news.all')}
			</Button>
			<Button
				aria-label={t('news.showInterest')}
				onClick={() => onScopeChange('interest')}
				size="xs"
				type="button"
				variant={isInterested ? 'secondary' : 'outline'}
			>
				{t('news.interest')}
			</Button>
		</div>
	);
}
